// Radio-station homepage content (RADIO venues, e.g. SWU.FM). Mirrors
// festival-content.ts: a hardcoded seed per venue slug, overridable by the
// admin-editable `appConfig.radio` blob. Kept loosely typed at the resolver
// boundary to avoid a types.ts import cycle.

import type { DayIndex, ScheduleEntry } from "./radio-schedule";

export type RadioShow = {
  name: string;
  /** "with <guest>" line, or a short descriptor. */
  with?: string;
  /** Human date/time label, e.g. "Fri 11 Jul · 23:00". */
  when?: string;
  /** Duration for archive episodes, e.g. "1:58:00". */
  duration?: string;
  genres?: string[];
  /** Optional cover image URL, falls back to the dot-logo watermark tile. */
  image?: string;
};

export type RadioResident = {
  name: string;
  initials: string;
  genres?: string;
  image?: string;
};

export type RadioEvent = {
  date: string;
  name: string;
  lineup?: string;
  /** Ticket URL (an Assembly event page) or "#". */
  href?: string;
};

export type RadioProduct = { name: string; price: string; href?: string };

export type RadioTier = {
  name: string;
  price: string;
  features: string[];
  featured?: boolean;
};

export type RadioContent = {
  /** Station name shown in the wordmark, e.g. "SWU". Suffix rendered separately. */
  wordmark: string;
  suffix?: string; // e.g. ".FM"
  /** Optional one-liner under the wordmark. Omit for a clean, logo-only hero. */
  tagline?: string;
  /** IANA timezone the schedule times are in (default Europe/London). */
  timezone?: string;
  /** HLS/Icecast/stream URL for the live <audio> element. Empty → silent demo. */
  streamUrl?: string;
  /** Optional now-playing API (e.g. AzuraCast /api/nowplaying/<station>), polled
   *  to show the live track under the scheduled show. */
  nowPlayingUrl?: string;
  /** Weekly recurring schedule, "what's on air now" is derived from the clock. */
  schedule: ScheduleEntry[];
  /** Fallback on-air label when the schedule has no match (e.g. off air). */
  live?: { name: string; with?: string; since?: string; genres?: string[] };
  highlights: RadioShow[];
  residents: RadioResident[];
  archive: RadioShow[];
  events: RadioEvent[];
  shop: RadioProduct[];
  support: RadioTier[];
  socials: { label: string; href: string }[];
  location?: string;
};

// 24/7 weekly grid: overnight + daytime rotation with live residents in prime
// time (18:00–00:00). The rotation fills the rest so something is always on air.
const ROTATION: Array<Omit<ScheduleEntry, "day">> = [
  { start: "00:00", end: "06:00", show: "SWU Overnight", genres: ["Bass", "Ambient"] },
  { start: "06:00", end: "12:00", show: "SWU Rotation", genres: ["Selections"] },
  { start: "12:00", end: "18:00", show: "SWU Daytime", genres: ["Bass", "House"] },
];
// Three prime-time slots per day: 18:00–20:00, 20:00–22:00, 22:00–00:00.
const EVENINGS: Record<DayIndex, Array<[string, string | undefined, string[]]>> = {
  0: [["Chloe Sage", undefined, ["Liquid", "Soul"]], ["Boogie Cafe", "Sean McCabe", ["Soulful House"]], ["Viridity", "Kathryn Brenna", ["Ambient", "Experimental"]]],
  1: [["Sofa Sound Bristol", "Sweetpea + T>I", ["Jungle", "D&B"]], ["Kyrist", "b2b Klinical", ["Neurofunk"]], ["0079", undefined, ["Dubstep", "Experimental"]]],
  2: [["Hazel Marimba", "SGT Pokes", ["Dub", "Reggae"]], ["Josi Devil", undefined, ["Dubstep", "140"]], ["LMajor", undefined, ["Grime", "Bass"]]],
  3: [["TOYS Berlin", "ASA 808 & Ini Lamborghini", ["Bass", "House"]], ["Cheff The Boy", undefined, ["Jump Up"]], ["Marcus Nasty", undefined, ["UK Funky"]]],
  4: [["Viridity", "Kathryn Brenna", ["Experimental"]], ["Kyrist", undefined, ["Neurofunk"]], ["Sofa Sound Bristol", "Sweetpea + T>I", ["Jungle", "D&B"]]],
  5: [["Josi Devil", undefined, ["Dubstep", "140"]], ["Marcus Nasty", undefined, ["UK Funky"]], ["Cheff The Boy", undefined, ["Jump Up"]]],
  6: [["Boogie Cafe", "Sean McCabe", ["Soulful House"]], ["Hazel Marimba", "SGT Pokes", ["Dub", "Reggae"]], ["0079", undefined, ["Dubstep", "Experimental"]]],
};
function buildWeek(): ScheduleEntry[] {
  const slots: Array<[string, string]> = [["18:00", "20:00"], ["20:00", "22:00"], ["22:00", "00:00"]];
  const out: ScheduleEntry[] = [];
  for (let d = 0 as DayIndex; d <= 6; d = (d + 1) as DayIndex) {
    for (const r of ROTATION) out.push({ day: d, ...r });
    EVENINGS[d].forEach(([show, host, genres], i) => {
      out.push({ day: d, start: slots[i][0], end: slots[i][1], show, host, genres });
    });
  }
  return out;
}

const SWU: RadioContent = {
  wordmark: "SWU",
  suffix: ".FM",
  location: "Bristol, UK",
  timezone: "Europe/London",
  streamUrl: "", // TODO(swu): drop in the live Icecast/Radio.co URL to go on air
  schedule: buildWeek(),
  highlights: [
    { name: "TOYS Berlin", with: "with ASA 808 & Ini Lamborghini", when: "11/07/2026 · 23:00", genres: ["Bass", "House", "Garage"] },
    { name: "Viridity", with: "with Kathryn Brenna", when: "12/07/2026 · 19:00", genres: ["Drum & Bass", "Liquid"] },
    { name: "Marcus Nasty, Birthday Special", with: "with Suspectacular & DJ Statix", when: "16/07/2026 · 14:00", genres: ["Funky", "Amapiano"] },
    { name: "Sofa Sound Bristol", with: "with Sweetpea + T>I", when: "15/07/2026 · 19:00", genres: ["Drum & Bass", "Jungle", "Jump Up"] },
    { name: "Hazel Marimba", with: "with SGT Pokes", when: "18/07/2026 · 14:00", genres: ["Bass", "Drum & Bass", "Dubstep"] },
    { name: "Cheff The Boy", with: "SWU resident", when: "17/07/2026 · 20:00", genres: ["Jungle", "Hardcore", "Breakbeat"] },
  ],
  residents: [
    { name: "0079", initials: "00", genres: "Dubstep · Experimental" },
    { name: "LMajor", initials: "LM", genres: "Grime · Bass" },
    { name: "TOYS Berlin", initials: "TB", genres: "Bass · House · Garage" },
    { name: "Sofa Sound Bristol", initials: "SSB", genres: "Jungle · D&B" },
    { name: "Kyrist", initials: "KY", genres: "Neurofunk" },
    { name: "Josi Devil", initials: "JD", genres: "Dubstep · 140" },
    { name: "Hazel Marimba", initials: "HM", genres: "Dub · Reggae" },
    { name: "Boogie Cafe", initials: "BC", genres: "Soulful House" },
    { name: "Viridity", initials: "VR", genres: "Experimental" },
    { name: "Cheff The Boy", initials: "CB", genres: "Jump Up" },
    { name: "Marcus Nasty", initials: "MN", genres: "UK Funky" },
  ],
  archive: [
    { name: "Marcus Nasty, Birthday Special", genres: ["UK Funky", "Bassline"], when: "02 Aug 2026", duration: "2:30:00" },
    { name: "Hazel Marimba + SGT Pokes", genres: ["Reggae", "Dub", "Jungle"], when: "31 Jul 2026", duration: "1:58:00" },
    { name: "Boogie Cafe w/ Sean McCabe", genres: ["Soulful", "Deep House"], when: "29 Jul 2026", duration: "2:00:00" },
    { name: "Viridity + Kathryn Brenna", genres: ["Ambient", "Experimental"], when: "27 Jul 2026", duration: "1:30:00" },
    { name: "Kyrist b2b Klinical", genres: ["Neurofunk", "Deep D&B"], when: "25 Jul 2026", duration: "2:00:00" },
  ],
  events: [
    { date: "SAT 20 SEP", name: "SWU presents: Warehouse", lineup: "Kyrist · Josi Devil · Cheff" },
    { date: "FRI 03 OCT", name: "SWU x Sofa Sound: Jungle", lineup: "Sweetpea · T>I · Hazel" },
    { date: "SAT 25 OCT", name: "Marcus Nasty Birthday Rave", lineup: "Marcus Nasty + guests" },
  ],
  shop: [
    { name: "SWU.FM Logo Tee, Black", price: "£28.00" },
    { name: "Sub Heavy Hoodie, Violet", price: "£55.00" },
    { name: "SWU Dubplate Series Vol.1", price: "£18.00" },
    { name: "Bristol 24/7 Cap, Black", price: "£22.00" },
  ],
  support: [
    { name: "Listener", price: "Free", features: ["Live stream, always on", "Full show archive", "Weekly schedule mailer"] },
    { name: "Supporter", price: "£5 /mo", featured: true, features: ["Everything in Listener", "Ad-free streaming", "Early mix downloads", "10% off shop & tickets"] },
    { name: "Crew", price: "£12 /mo", features: ["Everything in Supporter", "Guestlist to SWU events", "Members' shows", "Members' NFC card"] },
  ],
  socials: [
    { label: "Instagram · @swu.fm", href: "https://instagram.com/swu.fm" },
    { label: "Twitter · @swufm", href: "https://twitter.com/swufm" },
    { label: "SoundCloud · swufm", href: "https://soundcloud.com/swufm" },
    { label: "YouTube · swufm", href: "https://youtube.com/@swufm" },
  ],
};

const BY_SLUG: Record<string, RadioContent> = { swu: SWU };

// Resolve the content for a venue: admin-editable appConfig.radio wins, then
// the hardcoded seed for the venue slug.
export function resolveRadioContent(
  venue:
    | {
        slug?: string;
        appConfig?:
          | { radio?: RadioContent; radioStream?: { streamUrl?: string; nowPlayingUrl?: string } }
          | null;
      }
    | null
    | undefined,
): RadioContent | null {
  const base = venue?.appConfig?.radio ?? (venue?.slug ? BY_SLUG[venue.slug] ?? null : null);
  if (!base) return null;
  // Streaming config is admin-editable data (appConfig.radioStream) layered over
  // the content seed, so a station can go on air without a redeploy.
  const s = venue?.appConfig?.radioStream;
  if (!s) return base;
  return {
    ...base,
    streamUrl: s.streamUrl ?? base.streamUrl,
    nowPlayingUrl: s.nowPlayingUrl ?? base.nowPlayingUrl,
  };
}
